// !NOIMPORT (until unify kendo) import "@kendo/kendo.data.js";
import ObservableArray = kendo.data.ObservableArray;
import Model = kendo.data.Model;
import Observable = kendo.Observable;
import GridColumn = kendo.ui.GridColumn;
import { CompatibilityService } from '../services/compatibilityservice';
export { Model, ObservableArray, Observable, GridColumn }

/** Description for a kendo. Observable like Obj */

export interface IObservable<T = any> {
    get<K extends keyof T>(key: K): T[K];
    set<K extends keyof T>(key: K, value: T[K]): void;

    bind<K extends keyof T>(eventName: "change", handler: ((e: Readonly<{ field: K, sender: IObservable<T> }>) => any)): this;
    bind(eventName: string, handler: Function): this;
    one(eventName: string, handler: Function): this;
    first(eventName: string, handler: Function): this;
    trigger(eventName: string, e?: any): boolean;
    unbind(eventName: string, handler?: any): this;
}

/** Typed Description of a Kendo Observable Array */
export interface IObservableArray<T = any> extends ArrayLike<T>, IObservable {
    push(...items: T[]): number;
    remove(item: T): void;
    /**
     * Removes the first element from an array and returns it.
     */
    shift(): T;
    /**
     * Returns a section of an array.
     * @param begin The beginning of the specified portion of the array.
     * @param end The end of the specified portion of the array.
     */
    slice(begin: number, end?: number): T[];
    indexOf(item: T): number;
    /**
     * Removes elements from an array and, if necessary, inserts new elements in their place, returning the deleted elements.
     * @param start The zero-based location in the array from which to start removing elements.
     */
    splice(start: number): T[];
    /**
     * Removes elements from an array and, if necessary, inserts new elements in their place, returning the deleted elements.
     * @param start The zero-based location in the array from which to start removing elements.
     * @param deleteCount The number of elements to remove.
     * @param items Elements to insert into the array in place of the deleted elements.
     */
    splice(start: number, deleteCount: number, ...items: T[]): T[];
    filter(callback: (item: T, index: number, source: IObservableArray<T>) => boolean): T[];
    find(callback: (item: T, index: number, source: IObservableArray<T>) => boolean): T;
    forEach(callback: (item: T, index: number, source: IObservableArray<T>) => void): void;
    /**
    * Calls a defined callback function on each element of an array, and returns an array that contains the results.
        * @param callbackfn A function that accepts up to three arguments. The map method calls the callbackfn function one time for each element in the array.
        * @param thisArg An object to which the this keyword can refer in the callbackfn function. If thisArg is omitted, undefined is used as the this value.
        */
    map<TResult>(callback: (item: T, index: number, source: IObservableArray<T>) => TResult): TResult[];
    /** revert to Base JSON Obj Array */
    toJSON(): T[];
}

export interface IEvent {
    sender: any;
    isDefaultPrevented(): boolean;
    preventDefault: () => void;
}

/** Deferred Events Util extension on Observabled */
export function DeferredObservable<TObservable extends new (...args: any[]) => Observable>(Base: TObservable) {
    return class extends Base {

        /** Trigger Event, enabling Registrations of Deferred Interceptors */
        protected triggerWithDefer<T>($c: CompatibilityService, name: string, exEvent?: T): PromiseLike<T> {

            var deferreds = new Array<(e: any) => PromiseLike<any>>();
            var event = this.triggerEvent(name, {
                EnqueueDefer: (resolveDefer: () => PromiseLike<any>) => deferreds.push(resolveDefer)
            });

            if (event.isDefaultPrevented())
                return $c.reject("Rejected");

            try {
                // Chain Resolution of Promises (all might have UserInteraction, so do not start them in parlell!)
                return deferreds.reduce((previous: PromiseLike<any>, currResolve) =>
                    previous.then<any>(() => currResolve && currResolve(exEvent)), $c.when<any>(undefined)).then(() => exEvent);
            } catch (err) {
                return $c.reject(err);
            }
        }

        /** Register Defer Action interceptor */
        protected bindWithDefer(resolvePromise: (eOuter?: any) => (PromiseLike<any> | void), ...names: string[]) {
            var bindAction = (e) => e.EnqueueDefer(resolvePromise.bind(e));
            names.forEach(name => this.bind(name, bindAction));
            return () => { names.forEach(name => this.unbind(name, bindAction)); };
        }

        /** trigger Event */
        protected triggerEvent(name: string, event?: any): IEvent {
            this.trigger(name, event = this.ToEvent(event, this));
            return event;
        }

        protected ToEvent<T>(e: T, sender: any): T & IEvent {
            var result = <IEvent & T>(e || <any>{});

            if (!(result.isDefaultPrevented && result.preventDefault && result.sender)) {
                var _isPrevented = false;
                result.sender = sender;
                result.preventDefault = () => { _isPrevented = true; };
                result.isDefaultPrevented = () => _isPrevented;
            }

            return result;
        }
    };
}
